import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import SEO from "@/components/SEO";
import Breadcrumbs, { breadcrumbSchema } from "@/components/Breadcrumbs";
import FAQ, { faqSchema } from "@/components/FAQ";
import { KineticLines } from "@/components/Motion";
import { ProductCard } from "@/components/Cards";
import CTASection from "@/components/CTASection";
import NotFound from "@/pages/NotFound";
import { FileDown } from "lucide-react";
import api from "@/lib/api";
import { trackEvent } from "@/lib/analytics";

export default function ProductCategory() {
  const { slug } = useParams();
  const [category, setCategory] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    Promise.all([api.get("/categories"), api.get(`/products?category=${slug}`)])
      .then(([cats, prods]) => {
        setCategory(cats.data.find((c) => c.slug === slug) || null);
        setProducts(prods.data);
      })
      .catch(() => setCategory(null))
      .finally(() => setLoading(false));
  }, [slug]);

  if (!loading && !category) return <NotFound />;
  if (!category) return <div className="min-h-[50vh]" />;

  const crumbs = [{ label: "Products", to: "/products" }, { label: category.name }];

  return (
    <div data-testid="category-page">
      <SEO title={category.name} path={`/products/category/${slug}`} image={category.image}
        description={category.description}
        jsonLd={[breadcrumbSchema(crumbs), ...(category.faqs?.length ? [faqSchema(category.faqs)] : [])]} />
      <section className="blueprint-dark bg-ink">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-24">
          <Breadcrumbs dark items={crumbs} />
          <p className="eyebrow eyebrow-light">Product Category</p>
          <KineticLines lines={[category.name]}
            className="mt-5 font-display text-4xl font-medium leading-[1.06] tracking-tight text-white sm:text-5xl" />
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-400">{category.description}</p>
          <div className="mt-8 flex flex-wrap items-center gap-4">
            <Link to={`/request-a-quote?product=${encodeURIComponent(category.name)}`} data-testid="category-rfq-button"
              className="group inline-flex items-center gap-2 bg-accent px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-accent-dark">
              Request a Quote <ArrowRight size={15} className="transition-transform group-hover:translate-x-0.5" />
            </Link>
            <a href="/STJ-Innovations-Pharmaceutical-Cleanroom-Equipment-Catalogue.pdf" target="_blank" rel="noopener noreferrer"
              data-testid="category-catalogue-button"
              onClick={() => trackEvent("datasheet_download", { document: "product_catalogue", from: `category:${slug}` })}
              className="inline-flex items-center gap-2 border border-white/20 px-6 py-3 text-sm font-semibold text-white transition-colors hover:border-white/50">
              <FileDown size={15} /> Download Catalogue
            </a>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-24">
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="category-products-grid">
          {products.map((p, i) => <ProductCard key={p.slug} product={p} index={i} />)}
        </div>
        {products.length === 0 && (
          <p className="border border-line bg-mist p-10 text-center text-sm text-steel">
            Products in this category are being documented. Contact us with your requirement — custom solutions are our strength.
          </p>
        )}
      </section>

      {category.faqs?.length > 0 && (
        <section className="mx-auto max-w-4xl px-4 pb-20 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-semibold tracking-tight text-ink sm:text-3xl">
            Frequently Asked Questions
          </h2>
          <div className="mt-8"><FAQ items={category.faqs} /></div>
        </section>
      )}

      <CTASection />
    </div>
  );
}
